import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  type MouseEvent,
  type SyntheticEvent,
} from 'react';
import { Redirect, Route, Router, Switch, useLocation } from 'wouter';
import {
  INTERNAL_HOSTS,
  contentRoutes,
  normalizeRoute,
  preloadRoute,
} from '@/lib/content';
import ErrorBoundary from '@/components/ErrorBoundary';
import Layout from '@/components/Layout';
import Home from '@/pages/Home';
import { MEDICAL_OFFICE_ROUTES } from '@/lib/medical-family-office';
import { isGermanyPath, swissPath, germanyPath } from '@/lib/markets';
import { MarketContext, useMarket } from '@/lib/market-context';

const ContentPage = lazy(() => import('@/pages/ContentPage'));
const Archive = lazy(() => import('@/pages/Archive'));
const Portfolios = lazy(() => import('@/pages/Portfolios'));
const SalesDashboard = lazy(() => import('@/pages/SalesDashboard'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const BusinessCase = lazy(() => import('@/pages/BusinessCase'));
const DatenschutzBgm = lazy(() => import('@/pages/DatenschutzBgm'));
const BgmZahlenSchweiz = lazy(() => import('@/pages/bgm-zahlen-schweiz'));
const BgmEinfuehren = lazy(() => import('@/pages/BgmEinfuehren'));
const WorkplaceSolutions = lazy(() => import('@/pages/WorkplaceSolutions'));
const PaulMohacsi = lazy(() => import('@/pages/PaulMohacsi'));
const NoelSchulz = lazy(() => import('@/pages/NoelSchulz'));
const MedicalFamilyOffice = lazy(() => import('@/pages/medical-family-office'));
const GermanyPage = lazy(() => import('@/pages/germany'));

/** Routes that have a bespoke component instead of the generic page view. */
const BESPOKE = new Set(['/', '/sales-dashboard', '/workplace-solutions']);
const genericRoutes = contentRoutes.filter((r) => !BESPOKE.has(r));

/** A same-origin path that points at a real file, not at a route. */
function looksLikeFile(pathname: string): boolean {
  return /\.[a-z0-9]{2,5}$/i.test(pathname);
}

/** The internal route an anchor points at, or null if it is not one. */
function routeOf(anchor: Element): string | null {
  const href = anchor.getAttribute('href');
  if (!href) return null;
  if (/^(mailto:|tel:|sms:|javascript:|data:|blob:)/i.test(href)) return null;
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }
  const sameSite = url.origin === window.location.origin || INTERNAL_HOSTS.has(url.hostname);
  // Country changes must reach the other domain, never render Swiss content
  // under an international URL (or vice versa).
  if (url.origin !== window.location.origin && /^https?:/i.test(href) &&
      (isGermanyPath(window.location.pathname) || url.hostname === 'health-prime.org')) return null;
  if (!sameSite) return null;
  if (looksLikeFile(url.pathname)) return null;
  return normalizeRoute(url.pathname);
}

export default function App() {
  const [path, navigate] = useLocation();
  const germany = isGermanyPath(path);
  const hook = () => [germany ? swissPath(path) : path, (to: string, options?: { replace?: boolean }) => navigate(germany ? germanyPath(to) : to, options)] as ReturnType<typeof useLocation>;
  return <MarketContext.Provider value={{ germany, path }}><Router hook={hook}><SiteApp /></Router></MarketContext.Provider>;
}

function SiteApp() {
  const { germany } = useMarket();
  const [location, navigate] = useLocation();

  /**
   * One delegated handler for every anchor the app renders, including the ones
   * inside `dangerouslySetInnerHTML` content. Internal links route through
   * wouter; modified clicks, new tabs, downloads and external targets are left
   * to the browser.
   */
  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      const anchor = target?.closest?.('a');
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.hasAttribute('data-native-link')) return;
      const anchorTarget = anchor.getAttribute('target');
      if (anchorTarget && anchorTarget !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href) return;
      const path = routeOf(anchor);
      if (path === null) return;
      const url = new URL(href, window.location.href);

      // A pure fragment on the current page: let the browser handle it.
      if (url.hash && path === normalizeRoute(window.location.pathname)) return;

      event.preventDefault();
      navigate(path + url.search + url.hash);
    },
    [navigate],
  );

  /**
   * Hovering or focusing an internal link starts that page's content chunk,
   * so following the link usually costs no wait at all. Failures are ignored:
   * the render that needs the copy will ask again.
   */
  const onIntent = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]');
    if (!anchor) return;
    const path = routeOf(anchor);
    if (path) preloadRoute(path);
  }, []);

  // Scroll behaviour on navigation: to the fragment, or back to the top.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash) {
      const node = document.getElementById(decodeURIComponent(hash));
      if (node) {
        node.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className="hp-app" onClick={onClick} onPointerOver={onIntent} onFocus={onIntent}>
      <Layout>
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Switch>
              {(germany ? ['/bgm-zahlen-schweiz', '/business-case-bgm', '/datenschutz-bgm', '/bgm-einfuehren'] : []).map((route) => (
                <Route key={route} path={route} component={GermanyPage} />
              ))}
              <Route path="/" component={Home} />
              <Route path="/homepage-main">{() => <Redirect to="/" replace />}</Route>
              <Route path="/sales-dashboard" component={SalesDashboard} />
              <Route path="/workplace-solutions" component={WorkplaceSolutions} />
              <Route path="/business-case-bgm" component={BusinessCase} />
              <Route path="/datenschutz-bgm" component={DatenschutzBgm} />
              <Route path="/bgm-zahlen-schweiz" component={BgmZahlenSchweiz} />
              <Route path="/bgm-einfuehren" component={BgmEinfuehren} />
              <Route path="/paul-mohacsi" component={PaulMohacsi} />
              <Route path="/noel-schulz" component={NoelSchulz} />
              {MEDICAL_OFFICE_ROUTES.map((route) => (
                <Route key={route} path={route}>
                  <MedicalFamilyOffice key={route} />
                </Route>
              ))}
              <Route path="/portfolios" component={Portfolios} />
              <Route path="/portfolio-category/:slug" component={Archive} />
              <Route path="/category/:slug" component={Archive} />
              <Route path="/tag/:slug" component={Archive} />
              <Route path="/author/:slug" component={Archive} />
              {genericRoutes.map((route) => (
                <Route key={route} path={route} component={ContentPage} />
              ))}
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </Layout>
    </div>
  );
}
